import React, { useState, useEffect } from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TextField, Button, CircularProgress
} from '@mui/material';
import { Print as PrintIcon, Description as DescriptionIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import { useSucursal } from '../contexts/SucursalContext';
import ConteoDenominaciones from './ConteoDenominaciones';



const CierreCaja = () => {
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;
  const { user } = useUser();
  const { sucursalSeleccionada } = useSucursal();
  // Estado para fecha y hora actual
  const [currentDateTime, setCurrentDateTime] = useState(new Date().toLocaleString());

  // Estados para la sección "Saldo de Transacciones según Cajero"
  const [transaccionesCajero, setTransaccionesCajero] = useState([]);
  const [totalValorCajero, setTotalValorCajero] = useState(0);
  const [totalComisionCajero, setTotalComisionCajero] = useState(0);
  const [diferenciaCajero, setDiferenciaCajero] = useState(0);

  // Estados para la sección "Saldos Transacciones según Sistema"
  const [detalleSistema, setDetalleSistema] = useState([]);
  const [transaccionesSistema, setTransaccionesSistema] = useState([]);
  const [totalValorSistema, setTotalValorSistema] = useState(0);
  const [totalComisionSistema, setTotalComisionSistema] = useState(0);
  // Estados para la sección "Supervisado por"
  const [supervisorUsuario, setSupervisorUsuario] = useState('');
  const [supervisorPassword, setSupervisorPassword] = useState('');
  const { cajaAbiertaDatos, actualizarCajaAbierta, cajaAbierta } = useSucursal();
  
  // Estado para bloquear botón de guardar
  const [guardando, setGuardando] = useState(false);

  // Estados para conteo de denominaciones
  const [showConteoDenominaciones, setShowConteoDenominaciones] = useState(false);
  const [conteoCompletado, setConteoCompletado] = useState(false);
  
  // Estados para la impresión
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Actualizar fecha/hora cada minuto (opcional)
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentDateTime(now.toLocaleString());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Cargar datos de transacciones para la sección "según Cajero"
  useEffect(() => {
    if (cajaAbierta) {
      // Actualizar el estado de caja (esto se hace para forzar la actualización, si se requiere)
      actualizarCajaAbierta();

      const fetchTransaccionesCajero = async () => {
        try {
          const response = await fetch(`${server}/MgCajaTipoTransaccions`);
          const data = await response.json();
          const arr = data.$values || data || [];
          console.log("Transacciones API:", arr);
          // Filtrar para incluir solo los tipos de transacción con mostrarEnCierreCaja === true
          const filtrado = arr.filter(item => item.mostrarEnCierreCaja === true);
          
          // Ordenar por el campo orden
          const ordenado = filtrado.sort((a, b) => (a.orden || 0) - (b.orden || 0));
          const procesado = ordenado.map(item => ({
            ...item,
            descripcionTransaccion: 'Total ' + item.descripcionTipoTransaccion,
            valorInput: '',  // Initialize as empty string instead of 0
            comisionCalculada: 0,
            valorSistema: 0
          }));
          setTransaccionesCajero(procesado);
          setTransaccionesSistema(procesado);
        } catch (error) {
          console.error('Error al cargar transacciones (cajero):', error);
        }
      };

      fetchTransaccionesCajero();
    }
  }, [cajaAbierta, actualizarCajaAbierta]);


  // Cargar detalle del sistema según el idEncabezado
  // Cargar detalle del sistema solo si cajaAbiertaDatos existe y la caja está abierta
  useEffect(() => {
    if (cajaAbierta && cajaAbiertaDatos) {
      const fetchDetalleSistema = async () => {
        try {
          const response = await fetch(`${server}/MgCajaArqueoDetalleSistemas/porEncabezadoSumado/${cajaAbiertaDatos}`);
          const data = await response.json();
          const arr = data.$values || data || [];
          setDetalleSistema(arr);
          console.log("Detalle sistema:", arr);
        } catch (error) {
          console.error('Error al cargar detalle sistema:', error);
        }
      };
      fetchDetalleSistema();
    }
  }, [cajaAbierta, cajaAbiertaDatos]);

  // Actualizar transacciones para la sección "según Sistema"
  useEffect(() => {
    if (detalleSistema.length > 0 && transaccionesCajero.length > 0) {
      const actualizado = transaccionesCajero.map(row => {
        const match = detalleSistema.find(det => det.idTipoTransaccion === row.id);
        return { ...row, valorSistema: match ? match.totalValor : 0 };
      });
      setTransaccionesSistema(actualizado);
      const totalSys = actualizado.reduce((sum, row) => sum + (row.egreso ? -Number(row.valorSistema || 0) : Number(row.valorSistema || 0)), 0);
      setTotalValorSistema(totalSys);
      const totalComSys = actualizado.reduce((sum, row) => {
        const comisionValue = row.comision > 0 ? Number(row.valorSistema) * (Number(row.comision) / 100) : 0;
        return sum + comisionValue;
      }, 0);
      setTotalComisionSistema(totalComSys);
    }
  }, [detalleSistema, transaccionesCajero]);

  // Manejar cambio en el campo "valor" editable de la tabla de transacciones según Cajero.
  const handleValorChange = (id, value) => {
    // No permitir valores negativos
    if (value !== '' && parseFloat(value) < 0) {
      return;
    }
    
    const actualizado = transaccionesCajero.map(row => {
      if (row.id === id) {
        // Store the raw input value (could be empty string)
        const valorNumeric = value === '' ? 0 : parseFloat(value) || 0;
        const comisionCalculada = valorNumeric * ((row.comision != null && row.comision > 0) ? (row.comision / 100) : 0);
        return { ...row, valorInput: value, comisionCalculada };
      }
      return row;
    });
    setTransaccionesCajero(actualizado);
    
    // For calculations, convert empty strings to 0
    const totalValor = actualizado.reduce((sum, row) => {
      const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
      return sum + (row.egreso ? -valor : valor);
    }, 0);
    const totalComision = actualizado.reduce((sum, row) => sum + row.comisionCalculada, 0);
    setTotalValorCajero(totalValor);
    setTotalComisionCajero(totalComision);
    setDiferenciaCajero(totalValor - totalComision);
  };

  // Función para Guardar
  const handleGuardar = async () => {
    if (guardando) return;
    setGuardando(true);

    if (!conteoCompletado) {
      Swal.fire('Advertencia', 'Debe completar el conteo físico de denominaciones antes de guardar el cierre.', 'warning');
      return;
    }

    if (!cajaAbierta) {
      Swal.fire('Información', 'La caja está cerrada. No se pueden cargar los datos.', 'info');
      return;
    }

    // Validar que no todos los valores del cajero estén en cero si hay transacciones en el sistema
    const todosCerosEnCajero = transaccionesCajero.every(row => {
      const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
      return valor === 0;
    });

    const hayTransaccionesEnSistema = transaccionesSistema.some(row => {
      const valorSist = parseFloat(row.valorSistema) || 0;
      return valorSist !== 0;
    });

    if (todosCerosEnCajero && hayTransaccionesEnSistema) {
      Swal.fire({
        title: 'Advertencia',
        text: 'No puede realizar un cierre con todos los valores en cero cuando existen transacciones registradas en el sistema. Por favor, ingrese los valores correctos de su conteo.',
        icon: 'warning',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Validar que no haya valores negativos en el cajero
    const hayValoresNegativos = transaccionesCajero.some(row => {
      const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
      return valor < 0;
    });

    if (hayValoresNegativos) {
      Swal.fire({
        title: 'Error',
        text: 'No se permiten valores negativos en el cierre de caja. Por favor, corrija los valores ingresados.',
        icon: 'error',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Validar que la diferencia total sea cero (no se permiten faltantes ni sobrantes)
    const diferenciaTotalCierre = totalValorCajero - totalValorSistema;
    if (diferenciaTotalCierre !== 0) {
      Swal.fire({
        title: 'Diferencia detectada',
        text: `No se puede completar el cierre de caja porque existe una diferencia de ${diferenciaTotalCierre.toFixed(2)}. El valor reportado por el cajero debe coincidir exactamente con el valor del sistema.`,
        icon: 'error',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Validar que se ingresaron datos del supervisor
    if (!supervisorUsuario || !supervisorPassword) {
      Swal.fire('Advertencia', 'Debe ingresar el usuario y contraseña del supervisor para completar el cierre.', 'warning');
      return;
    }

    // Validar credenciales del supervisor contra el endpoint de login
    try {
      const loginResponse = await fetch(`${server}/sg_sucursal_usuario_perfil/autenticacionsucursalesbyusuario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Usuario: supervisorUsuario, Password: supervisorPassword })
      });

      if (!loginResponse.ok) {
        Swal.fire('Error', 'Usuario o contraseña del supervisor inválidos.', 'error');
        return;
      }
    } catch (error) {
      Swal.fire('Error', 'No se pudo validar al supervisor. Intente de nuevo.', 'error');
      return;
    }

    try {
      // 1. Obtener los datos actuales del encabezado con una solicitud GET
      const getResponse = await fetch(`${server}/MgCajaEncabezadoArqueos/${cajaAbiertaDatos}`);
      if (!getResponse.ok) {
        Swal.fire('Error', 'Error al obtener datos del encabezado de arqueo.', 'error');
        return;
      }
      const headerData = await getResponse.json();

      // 2. Construir el payload usando la data existente y solo actualizando los campos necesarios:
      //    - valorCierre: total del valor del cajero (totalValorCajero)
      //    - diferencia: totalValorCajero - totalValorSistema
      //    - comision: totalComisionCajero
      //    - fechahoracierre y fechahoraautorizacion: fecha y hora actuales
      //    - ingresadoPor: el usuario actual (o 'ADMIN' si no existe)
      console.log(headerData);
      const pad = (n) => String(n).padStart(2, '0');
      const now = new Date();
      const fechaCierre = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
      const newHeaderPayload = {
        id: cajaAbiertaDatos,
        usuarioAutoriza: supervisorUsuario.substring(0, 20),
        valorCierre: totalValorCajero,
        diferencia: totalValorCajero - totalValorSistema,
        comision: totalComisionCajero,
        fechahorainicio: headerData.fechahorainicio,
        fechahoracierre: fechaCierre,
        modificadoPor: user.codigoUsuario || 'ADMIN',
        valorInicio: headerData.valorInicio
      };
      console.log(newHeaderPayload);
      const putResponse = await fetch(`${server}/MgCajaEncabezadoArqueos/${cajaAbiertaDatos}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newHeaderPayload)
      });

      if (!putResponse.ok) {
        Swal.fire('Error', 'Error al actualizar encabezado de arqueo.', 'error');
        return;
      }
      // Eliminar registros existentes de detalle usuario para este arqueo antes de insertar nuevos
      const usuarioDelete = (user.codigoUsuario || 'ADMIN').substring(0, 20);
      const deleteResponse = await fetch(`${server}/MgCajaArqueoDetalleUsuarios/por-arqueo/${cajaAbiertaDatos}/${usuarioDelete}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      // Si el DELETE retorna 404, significa que no había registros previos (es válido continuar)
      if (!deleteResponse.ok && deleteResponse.status !== 404) {
        Swal.fire('Error', 'Error al limpiar registros anteriores de detalle usuario.', 'error');
        return;
      }

      // Filtrar solo aquellas transacciones cuyo valor ingresado es distinto de 0
      const transaccionesValidas = transaccionesCajero.filter(row => {
        const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
        return valor !== 0;
      });

      for (const row of transaccionesValidas) {
        const matchingRow = transaccionesSistema.find(item => item.id === row.id);
        const valorSistemaUtilizado = matchingRow ? matchingRow.valorSistema : row.valorSistema;
        // Convert empty strings to 0 for saving
        const valorInput = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
        
        const detallePayload = {
          idTipoTransaccion: row.id,
          valor: valorInput,
          valorSistema: valorSistemaUtilizado,
          comision: row.comisionCalculada,
          diferencia: valorInput - valorSistemaUtilizado,
          idArqueo: cajaAbiertaDatos,
          ingresadoPor: user.codigoUsuario || 'ADMIN',
        };
        console.log(detallePayload);
        const postResponse = await fetch(`${server}/MgCajaArqueoDetalleUsuarios`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(detallePayload)
        });

        if (!postResponse.ok) {
          Swal.fire('Error', `Error al guardar detalle de arqueo para la transacción ${row.id}`, 'error');
          return;
        }
      }
      actualizarCajaAbierta();
      Swal.fire('Éxito', 'Cierre de caja guardado correctamente.', 'success');

      resetFormularios();
    } catch (error) {
      console.error('Error en guardar cierre de caja:', error);
      Swal.fire('Error', 'Ocurrió un error al guardar el cierre de caja.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Función para iniciar el proceso de cierre
  const handleIniciarCierre = () => {
    if (!cajaAbierta) {
      Swal.fire('Información', 'La caja está cerrada. No se pueden cargar los datos.', 'info');
      return;
    }

    // Validar que no haya valores negativos
    const hayValoresNegativos = transaccionesCajero.some(row => {
      const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
      return valor < 0;
    });

    if (hayValoresNegativos) {
      Swal.fire({
        title: 'Error',
        text: 'No se permiten valores negativos. Por favor, corrija los valores antes de continuar.',
        icon: 'error',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Validar que no todos los valores estén en cero si hay transacciones en el sistema
    const todosCerosEnCajero = transaccionesCajero.every(row => {
      const valor = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
      return valor === 0;
    });

    const hayTransaccionesEnSistema = transaccionesSistema.some(row => {
      const valorSist = parseFloat(row.valorSistema) || 0;
      return valorSist !== 0;
    });

    if (todosCerosEnCajero && hayTransaccionesEnSistema) {
      Swal.fire({
        title: 'Advertencia',
        text: 'No puede iniciar el conteo físico con todos los valores en cero cuando existen transacciones registradas. Por favor, ingrese los valores de su conteo primero.',
        icon: 'warning',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Mostrar modal de conteo de denominaciones
    setShowConteoDenominaciones(true);
  };

  const handleConteoCompleto = () => {
    setShowConteoDenominaciones(false);
    setConteoCompletado(true);
    Swal.fire('Éxito', 'Conteo físico registrado. Ahora puede completar el cierre de caja.', 'success');
  };

  // Función para obtener vista previa del cierre
  const handleVistaPrevia = async () => {
    if (!cajaAbiertaDatos) {
      Swal.fire('Error', 'No hay información de caja disponible.', 'error');
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await fetch(`${server}/MgCajaEncabezadoArqueos/VistaPreviaCierre/${cajaAbiertaDatos}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener vista previa');
      }

      const data = await response.json();
      
      // Construir HTML para mostrar la vista previa
      let htmlContent = '<div style="text-align: left;">';
      
      if (data.arqueo) {
        htmlContent += '<h3>Información del Arqueo</h3>';
        htmlContent += '<table style="width: 100%; border-collapse: collapse;">';
        htmlContent += `<tr><td><strong>Cajero:</strong></td><td>${data.arqueo.cajero || 'N/A'}</td></tr>`;
        htmlContent += `<tr><td><strong>Supervisor:</strong></td><td>${data.arqueo.supervisor || 'N/A'}</td></tr>`;
        htmlContent += `<tr><td><strong>Sucursal:</strong></td><td>${data.arqueo.sucursal || 'N/A'}</td></tr>`;
        htmlContent += `<tr><td><strong>Fecha Inicio:</strong></td><td>${data.arqueo.fechaInicio || 'N/A'}</td></tr>`;
        htmlContent += `<tr><td><strong>Fecha Cierre:</strong></td><td>${data.arqueo.fechaCierre || 'N/A'}</td></tr>`;
        htmlContent += `<tr><td><strong>Valor Inicio:</strong></td><td>$${Number(data.arqueo.valorInicio || 0).toFixed(2)}</td></tr>`;
        htmlContent += `<tr><td><strong>Valor Cierre:</strong></td><td>$${Number(data.arqueo.valorCierre || 0).toFixed(2)}</td></tr>`;
        htmlContent += `<tr><td><strong>Diferencia:</strong></td><td style="color: ${data.arqueo.diferencia === 0 ? 'green' : data.arqueo.diferencia > 0 ? 'blue' : 'red'}; font-weight: bold;">$${Number(data.arqueo.diferencia || 0).toFixed(2)}</td></tr>`;
        htmlContent += '</table>';
      }

      if (data.comparacion && data.comparacion.length > 0) {
        htmlContent += '<h3 style="margin-top: 20px;">Comparación de Transacciones</h3>';
        htmlContent += '<table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">';
        htmlContent += '<thead><tr style="background-color: #f0f0f0;">';
        htmlContent += '<th style="border: 1px solid #ddd; padding: 8px;">Descripción</th>';
        htmlContent += '<th style="border: 1px solid #ddd; padding: 8px;">Sistema</th>';
        htmlContent += '<th style="border: 1px solid #ddd; padding: 8px;">Cajero</th>';
        htmlContent += '<th style="border: 1px solid #ddd; padding: 8px;">Diferencia</th>';
        htmlContent += '</tr></thead><tbody>';
        
        data.comparacion.forEach(item => {
          const diffColor = item.diferencia === 0 ? 'green' : item.diferencia > 0 ? 'blue' : 'red';
          htmlContent += '<tr>';
          htmlContent += `<td style="border: 1px solid #ddd; padding: 8px;">${item.descripcion}</td>`;
          htmlContent += `<td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$${Number(item.valorSistema || 0).toFixed(2)}</td>`;
          htmlContent += `<td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$${Number(item.valorCajero || 0).toFixed(2)}</td>`;
          htmlContent += `<td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: ${diffColor}; font-weight: bold;">$${Number(item.diferencia || 0).toFixed(2)}</td>`;
          htmlContent += '</tr>';
        });
        
        htmlContent += '</tbody></table>';
      }

      htmlContent += '</div>';

      Swal.fire({
        title: 'Vista Previa del Cierre',
        html: htmlContent,
        width: '800px',
        showCancelButton: true,
        confirmButtonText: 'Generar PDF',
        cancelButtonText: 'Cerrar',
        customClass: {
          popup: 'swal-wide'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          handleGenerarPDF();
        }
      });

    } catch (error) {
      console.error('Error al obtener vista previa:', error);
      Swal.fire('Error', error.message || 'No se pudo obtener la vista previa del cierre.', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Función para generar y descargar el PDF
  const handleGenerarPDF = async () => {
    if (!cajaAbiertaDatos) {
      Swal.fire('Error', 'No hay información de caja disponible.', 'error');
      return;
    }

    setLoadingPDF(true);
    try {
      const response = await fetch(`${server}/MgCajaEncabezadoArqueos/GenerarDocumentoCierre/${cajaAbiertaDatos}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar el documento');
      }

      const data = await response.json();

      if (data.success && data.pdfBase64) {
        // Convertir base64 a blob
        const byteCharacters = atob(data.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Crear enlace de descarga
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.fileName || `CierreCaja_${cajaAbiertaDatos}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        Swal.fire('Éxito', 'Documento de cierre generado y descargado correctamente.', 'success');
      } else {
        throw new Error(data.message || 'No se recibió el PDF correctamente');
      }

    } catch (error) {
      console.error('Error al generar PDF:', error);
      Swal.fire('Error', error.message || 'No se pudo generar el documento de cierre.', 'error');
    } finally {
      setLoadingPDF(false);
    }
  };

  // Función para imprimir directamente
  const handleImprimirCierre = async () => {
    if (!cajaAbiertaDatos) {
      Swal.fire('Error', 'No hay información de caja disponible.', 'error');
      return;
    }

    setLoadingPDF(true);
    try {
      const response = await fetch(`${server}/MgCajaEncabezadoArqueos/GenerarDocumentoCierre/${cajaAbiertaDatos}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar el documento');
      }

      const data = await response.json();

      if (data.success && data.pdfBase64) {
        // Convertir base64 a blob
        const byteCharacters = atob(data.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Crear URL y abrir en ventana nueva para imprimir
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
        } else {
          throw new Error('No se pudo abrir la ventana de impresión. Verifique que las ventanas emergentes estén permitidas.');
        }

      } else {
        throw new Error(data.message || 'No se recibió el PDF correctamente');
      }

    } catch (error) {
      console.error('Error al imprimir:', error);
      Swal.fire('Error', error.message || 'No se pudo imprimir el documento de cierre.', 'error');
    } finally {
      setLoadingPDF(false);
    }
  };

  const resetFormularios = () => {
    // Reset to empty strings, not zeros
    setTransaccionesCajero(transaccionesCajero.map(row => ({
      ...row,
      valorInput: '',
      comisionCalculada: 0
    })));
    setTotalValorCajero(0);
    setTotalComisionCajero(0);
    setDiferenciaCajero(0);

    // Limpiar detalle del sistema y transacciones relacionadas
    setDetalleSistema([]);
    setTransaccionesSistema([]);
    setTotalValorSistema(0);
    setTotalComisionSistema(0);

    // Limpiar campos de supervisión
    setSupervisorUsuario('');
    setSupervisorPassword('');

    // Si deseas reiniciar la fecha y hora al momento de resetear (opcional)
    setCurrentDateTime(new Date().toLocaleString());
  };


  return (
    <div className="right_col" role="main">
      <div className="x_content">
        {/* <div className="row"> */}
        {/* <div className="col-md-8 col-sm-7 offset-md-2"> */}
        {/* Sección: Usuario y Fecha/Hora Actual */}
        <h2>Cierre de Caja</h2>
        <div>
          <p><strong>Usuario:</strong> {user.codigoUsuario || 'ADMIN'}</p>
          <p><strong>Fecha y Hora:</strong> {currentDateTime}</p>
        </div>

        {/* Sección: Comparación Valor Sistema vs Valor Cajero */}
        <h3>Cierre de Caja - Comparación de Valores</h3>
        <TableContainer component={Paper} sx={{ maxWidth: '100%', mb: 2 }}>
          <Table size="small" sx={{ fontSize: '0.8rem' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Descripción</TableCell>
                <TableCell sx={{ py: 0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                  Valor Sistema
                </TableCell>
                <TableCell sx={{ py: 0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#fff3e0' }}>
                  Valor Cajero
                </TableCell>
                <TableCell sx={{ py: 0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#ffebee' }}>
                  Diferencia
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transaccionesCajero.map((row) => {
                const valorSistema = transaccionesSistema.find(item => item.id === row.id)?.valorSistema || 0;
                const valorCajero = row.valorInput === '' ? 0 : parseFloat(row.valorInput) || 0;
                const diferencia = valorCajero - valorSistema;
                
                return (
                  <TableRow key={row.id}>
                    <TableCell sx={{ py: 0.5 }}>{row.descripcionTransaccion}</TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'right', backgroundColor: '#f5f5f5' }}>
                      {Number(valorSistema).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={row.valorInput}
                        onChange={(e) => handleValorChange(row.id, e.target.value)}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        placeholder="0"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        py: 0.5, 
                        textAlign: 'right',
                        color: diferencia === 0 ? 'green' : diferencia > 0 ? 'blue' : 'red',
                        fontWeight: diferencia !== 0 ? 'bold' : 'normal'
                      }}
                    >
                      {diferencia.toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                <TableCell sx={{ py: 0.5 }}><strong>TOTALES</strong></TableCell>
                <TableCell sx={{ py: 0.5, textAlign: 'right' }}>
                  <strong>{totalValorSistema.toFixed(2)}</strong>
                </TableCell>
                <TableCell sx={{ py: 0.5, textAlign: 'right' }}>
                  <strong>{totalValorCajero.toFixed(2)}</strong>
                </TableCell>
                <TableCell 
                  sx={{ 
                    py: 0.5, 
                    textAlign: 'right',
                    color: (totalValorCajero - totalValorSistema) === 0 ? 'green' : 
                           (totalValorCajero - totalValorSistema) > 0 ? 'blue' : 'red',
                    fontWeight: 'bold',
                    fontSize: '1.1em'
                  }}
                >
                  {(totalValorCajero - totalValorSistema).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Sección: Supervisado por */}
        <h3>Supervisado por</h3>
        <div>
          <TextField
            label="Usuario"
            value={supervisorUsuario}
            onChange={(e) => setSupervisorUsuario(e.target.value)}
            margin="normal"
            size="small"
          />
          <TextField
            label="Contraseña"
            type="password"
            value={supervisorPassword}
            onChange={(e) => setSupervisorPassword(e.target.value)}
            margin="normal"
            size="small"
          />
        </div>        {/* Botones de acción */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleIniciarCierre} 
            size="small"
            disabled={!cajaAbierta || !tienePermiso('Realizar Conteo Caja')}
          >
            Realizar Conteo Físico
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleGuardar} 
            size="small"
            disabled={guardando || !conteoCompletado || !tienePermiso('Completar Cierre Caja')}
          >
            {guardando ? 'Guardando...' : 'Completar Cierre'}
          </Button>
          
          {/* Botones de impresión */}
          <Button 
            variant="outlined" 
            color="info" 
            onClick={handleVistaPrevia} 
            size="small"
            startIcon={loadingPreview ? <CircularProgress size={16} /> : <DescriptionIcon />}
            disabled={loadingPreview || cajaAbierta || !tienePermiso('Imprimir Cierre Caja')}
          >
            Vista Previa
          </Button>
          <Button 
            variant="outlined" 
            color="success" 
            onClick={handleGenerarPDF} 
            size="small"
            startIcon={loadingPDF ? <CircularProgress size={16} /> : <DescriptionIcon />}
            disabled={loadingPDF || cajaAbierta || !tienePermiso('Imprimir Cierre Caja')}
          >
            Descargar PDF
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleImprimirCierre} 
            size="small"
            startIcon={loadingPDF ? <CircularProgress size={16} /> : <PrintIcon />}
            disabled={loadingPDF || cajaAbierta || !tienePermiso('Imprimir Cierre Caja')}
          >
            Imprimir
          </Button>
        </div>
        {/* </div> */}
        {/* </div> */}
      </div>
      
      {/* Modal de conteo de denominaciones */}
      <ConteoDenominaciones
        open={showConteoDenominaciones}
        onClose={handleConteoCompleto}
        tipoOperacion="CIERRE"
        idCajaEncabezado={cajaAbiertaDatos}
      />
    </div>
  );
};

export default CierreCaja;
